import { useMemo } from "react";
import { Box, Text, Divider } from "@mantine/core";
import {
  calculateAgeFromDateOnly,
  formatDateOnlyForDisplay,
  parseDateOnly,
} from "../../../utils/dateOnly";
import { useLabTodayDateOnly } from "../../../hooks/useLabTodayDateOnly";

interface Props {
  caseData: any;
}

export default function PatientClientCardV2({ caseData }: Props) {
  const patient = caseData?.patient;
  const draft = caseData?.draftPatientData;
  const physician = caseData?.physician;
  const practice = caseData?.sendingPractice;
  const surgeryCenter = caseData?.surgeryCenterLab;
  const submittingLab = caseData?.submittingLab;
  const patientDateOfBirth = useMemo(
    () => parseDateOnly(patient?.dateOfBirth),
    [patient?.dateOfBirth]
  );
  const displayedDateOfBirth = useMemo(
    () => patientDateOfBirth ?? parseDateOnly(draft?.dateOfBirth),
    [draft?.dateOfBirth, patientDateOfBirth]
  );
  const labToday = useLabTodayDateOnly();

  const pName = patient
    ? `${patient.firstName} ${patient.lastName}`
    : draft
    ? `${draft.firstName || ""} ${draft.lastName || ""}`.trim() || "—"
    : "—";
  const dob = useMemo(
    () =>
      displayedDateOfBirth
        ? formatDateOnlyForDisplay(displayedDateOfBirth)
        : null,
    [displayedDateOfBirth]
  );
  const age = useMemo(
    () =>
      patientDateOfBirth
        ? `${calculateAgeFromDateOnly(patientDateOfBirth, labToday)}Y`
        : "",
    [labToday, patientDateOfBirth]
  );
  const gender =
    patient?.sex === "M"
      ? "Male"
      : patient?.sex === "F"
      ? "Female"
      : patient?.sex || "—";

  const address = patient?.address;
  const hasContact =
    patient?.mobilePhone ||
    patient?.homePhone ||
    patient?.workPhone ||
    patient?.email;
  const hasAddress =
    address?.street || address?.city || address?.state || address?.zip;

  return (
    <Box
      style={{
        background: "white",
        borderRadius: 12,
        border: "1px solid var(--mantine-color-gray-2)",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      <Box
        px="lg"
        py="sm"
        style={{
          borderBottom: "1px solid var(--mantine-color-gray-1)",
          background: "rgba(248, 249, 250, 0.5)",
        }}
      >
        <Text size="sm" fw={700} tt="uppercase" lts="0.05em" c="dimmed">
          Patient &amp; Client
        </Text>
      </Box>
      <Box p="lg">
        <Box mb="md">
          <Text fw={700} c="dark">
            {pName}
          </Text>
          {dob && (
            <Text size="xs" c="dimmed">
              DOB: {dob}
              {age ? ` (${age})` : ""}
            </Text>
          )}
          <Text size="xs" c="dimmed">
            Gender: {gender}
          </Text>
          {(patient?.medicalRecordNumber || draft?.medicalRecordNumber) && (
            <Text size="xs" c="dimmed">
              MRN: {patient?.medicalRecordNumber || draft?.medicalRecordNumber}
            </Text>
          )}
        </Box>
        {hasContact && (
          <>
            <Divider my="xs" />
            <Box>
              <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>
                Contact
              </Text>
              {patient?.mobilePhone && (
                <Text size="xs" c="dimmed">
                  Mobile: {patient.mobilePhone}
                </Text>
              )}
              {patient?.homePhone && (
                <Text size="xs" c="dimmed">
                  Home: {patient.homePhone}
                </Text>
              )}
              {patient?.workPhone && (
                <Text size="xs" c="dimmed">
                  Work: {patient.workPhone}
                </Text>
              )}
              {patient?.email && (
                <Text size="xs" c="dimmed">
                  Email: {patient.email}
                </Text>
              )}
            </Box>
          </>
        )}
        {hasAddress && (
          <>
            <Divider my="xs" />
            <Box>
              <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>
                Address
              </Text>
              {address?.street && (
                <Text size="xs" c="dimmed">
                  {address.street}
                </Text>
              )}
              {(address?.city || address?.state || address?.zip) && (
                <Text size="xs" c="dimmed">
                  {[address.city, address.state, address.zip]
                    .filter(Boolean)
                    .join(", ")}
                </Text>
              )}
            </Box>
          </>
        )}
        <Divider my="xs" />
        <Box mt="sm">
          {practice && (
            <Box mb="sm">
              <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                Sending Practice
              </Text>
              <Text size="sm" fw={500}>
                {practice.name}
              </Text>
            </Box>
          )}
          {surgeryCenter && (
            <Box mb="sm">
              <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                Surgery Center
              </Text>
              <Text size="sm" fw={500}>
                {surgeryCenter.name}
              </Text>
              {surgeryCenter.primaryPhone && (
                <Text size="xs" c="dimmed">
                  {surgeryCenter.primaryPhone}
                </Text>
              )}
            </Box>
          )}
          {submittingLab && (
            <Box mb="sm">
              <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                Submitting Lab
              </Text>
              <Text size="sm" fw={500}>
                {submittingLab.name}
              </Text>
              {submittingLab.primaryPhone && (
                <Text size="xs" c="dimmed">
                  {submittingLab.primaryPhone}
                </Text>
              )}
            </Box>
          )}
          {physician && (
            <Box>
              <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                Ordering Physician
              </Text>
              <Text size="sm" fw={500}>
                Dr. {physician.firstName} {physician.lastName}
              </Text>
              {physician.npi && (
                <Text size="xs" c="dimmed">
                  NPI: {physician.npi}
                </Text>
              )}
              {physician.phone && (
                <Text size="xs" c="dimmed">
                  Tel: {physician.phone}
                </Text>
              )}
              {physician.fax && (
                <Text size="xs" c="dimmed">
                  Fax: {physician.fax}
                </Text>
              )}
            </Box>
          )}
          {!practice && !physician && !surgeryCenter && !submittingLab && (
            <Text size="sm" c="dimmed" fs="italic">
              No provider information
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );
}
